import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { apiFetch } from "./utils/api";
import Login from "./Login";
import LeftNavbar from "./left_navbar";
import Header from "./Header";
import Holiday from "./Holiday";
import Leave_approvals from "./Leave_approvals";
import My_leave_requests from "./My_leave_requests";
import Timesheet_entry from "./Timesheet_entry";
import Timesheet_approvals from "./Timesheet_approvals";
import Timesheet_summary from "./Timesheet_summary";
import Leave_dashboard from "./Leave_dashboard";
import Dashboard from "./Dashboard";
import Shift from "./Shift";
import Assign_Shifts from "./Assign_Shifts";
import NoShift from "./NoShift";
import { DNewsAlertHost } from "./utils/dNews_Alert";

function App() {
  const [navSize, setNavSize] = useState("full");
  const location = useLocation();

  // ✅ Hide LeftNavbar on specific pages
  const path = location.pathname.toLowerCase();
  const shouldHideNavbar = path === "/login" || path === "/thank-you";

  // 🔒 Shift gate: employees with no shift are locked out of the whole app.
  //    Admin / Sub Admin always bypass (they must reach the Shift pages).
  //    "unknown" until resolved; fail OPEN on error so a missing migration
  //    doesn't lock everyone out.
  const [shiftGate, setShiftGate] = useState("unknown"); // "unknown" | "allowed" | "blocked"

  useEffect(() => {
    if (shouldHideNavbar) return; // don't probe on the login screen
    let active = true;
    (async () => {
      try {
        // Role from the same reliable endpoint the navbar uses. Admin / Sub Admin
        // ALWAYS bypass the lock so they can reach the Shift pages and assign shifts.
        const empRes = await apiFetch("/api/employee/logined_employee", { credentials: "include" });
        const empData = await empRes.json();
        const role = Array.isArray(empData) ? empData[0]?.emp_access_level : null;
        if (role === "Admin" || role === "Sub Admin") {
          if (active) setShiftGate("allowed");
          return;
        }
        // Everyone else must have a shift assigned.
        const res = await apiFetch("/api/shift/my-shift", { credentials: "include" });
        if (!res.ok) { if (active) setShiftGate("allowed"); return; } // fail open on server error
        const data = await res.json();
        if (active) setShiftGate(data?.has_shift ? "allowed" : "blocked");
      } catch {
        if (active) setShiftGate("allowed"); // fail open
      }
    })();
    return () => { active = false; };
  }, [shouldHideNavbar, location.pathname]);

  // ✅ Optional: force scroll to top on route change (helps prevent UI glitches)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <>
      {/* Toast + modal host for the sign-in page - mounted ONCE at the App root
          so it is present in BOTH branches below (the sign-in screen lives in
          the logged-out one) and survives every route change. Without it every
          dnToast call is silently swallowed. */}
      <DNewsAlertHost />
      {/* ✅ Render Login & Thank You separately */}
      {shouldHideNavbar ? (
        <Routes>
          <Route path="/" element={<Navigate to="/Login" />} />
          <Route path="/Login" element={<Login />} />
          {/* ✅ Unknown route → redirect to Login */}
          <Route path="*" element={<Navigate to="/Login" replace />} />
        </Routes>
      ) : (
        // ✅ Main layout only for logged-in sections
        <div className="main-layout">
          <LeftNavbar navSize={navSize} setNavSize={setNavSize} />
          <Header />
          <div className={`main-content ${navSize}`}>
            {shiftGate === "unknown" ? (
              <div style={{ padding: "60px", textAlign: "center", color: "#94a3b8" }}>Loading…</div>
            ) : shiftGate === "blocked" ? (
              <NoShift />
            ) : (
              <Routes>
                <Route path="/" element={<Navigate to="/Login" />} />
                <Route path="/Login" element={<Login />} />
                <Route path="/Dashboard" element={<Dashboard navSize={navSize} />} />
                <Route path="/timesheet-summary" element={<Timesheet_summary navSize={navSize} />} />
                <Route path="/Holiday" element={<Holiday navSize={navSize} />} />
                <Route path="/Leave_approvals" element={<Leave_approvals navSize={navSize} />} />
                <Route path="/My_leave_requests" element={<My_leave_requests navSize={navSize} />} />
                <Route path="/Timesheet_entry" element={<Timesheet_entry navSize={navSize} />} />
                <Route path="/Timesheet_approvals" element={<Timesheet_approvals navSize={navSize} />} />
                <Route path="/Leave_dashboard" element={<Leave_dashboard navSize={navSize} />} />
                <Route path="/Shift" element={<Shift navSize={navSize} />} />
                <Route path="/Assign_Shifts" element={<Assign_Shifts navSize={navSize} />} />
                {/* ✅ Unknown route → redirect to Login */}
                <Route path="*" element={<Navigate to="/Login" replace />} />
              </Routes>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default App;
